/**
 * JobBoard Page - Kanban board for job workflow management
 * Feature 009: Workshop Booking & Job-Tracking System
 *
 * T050: JobBoard page with Kanban view and job detail drawer
 * Updated: Support URL params for direct job/booking selection
 */

import { useState, useCallback, useEffect, useMemo } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  RefreshCw, Search, X, ChevronRight, ExternalLink,
  Car, User, Wrench, Phone, Mail, Clock,
  CheckCircle, AlertCircle, Loader2, MapPin, Shield, Award, LayoutGrid, Image,
  Info, Send,
} from 'lucide-react';
import { EstimateEditor, type EstimateLineItem } from '../components/jobs/EstimateEditor';
import { api } from '../lib/api';
import { JobBoard, type JobBoardItem, getStatusConfig } from '../components/jobs/JobBoard';
import { JobTimeline, type TimelineEventData } from '../components/jobs/JobTimeline';
import { StatusTransitionDialog } from '../components/booking/StatusTransitionDialog';
import { useJobs, useJobDetail, useJobTransition, useJobEstimate, useJobNote, useEstimateLines, useSaveEstimateLines, useResendJobEmail } from '../hooks/useJobs';
import { useWorkflows } from '../hooks/useWorkflowConfig';
import { cn } from '../lib/utils';
import { pageStyles, formStyles, iconSizes, buttonStyles } from '../lib/styles';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { SimpleTooltip } from '../components/ui/tooltip';
import type { JobStatus, StatusChecklistRequirement, WorkflowStatus } from '@leal-crm/shared';

// View filter options
type ViewFilter = 'all' | 'private' | 'insurance' | 'warranty';

const VIEW_FILTERS: { value: ViewFilter; label: string; icon: typeof Car }[] = [
  { value: 'all', label: 'Todos', icon: LayoutGrid },
  { value: 'private', label: 'Particular', icon: Car },
  { value: 'insurance', label: 'Seguro', icon: Shield },
  { value: 'warranty', label: 'Garantía', icon: Award },
];


export default function JobBoardPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  // View filter (by booking type)
  const [viewFilter, setViewFilter] = useState<ViewFilter>('all');

  // Search filter
  const [search, setSearch] = useState('');

  // Selected job for detail view
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);

  // Fetch workflows to get dynamic columns and colors
  const { data: workflows } = useWorkflows();

  // Compute columns from workflow configuration (showInJobboard: true, isActive: true)
  const { columns, workflowStatuses } = useMemo(() => {
    if (!workflows || workflows.length === 0) {
      return {
        columns: [],
        workflowStatuses: [] as WorkflowStatus[],
      };
    }

    const jobWorkflows = workflows.filter((workflow) => workflow.workflow.workflowType === 'job');

    // When viewing "all", show the UNION of jobboard statuses across all job workflows.
    // This prevents missing columns when private/insurance/warranty have extra statuses.
    const relevantWorkflows = viewFilter === 'all'
      ? jobWorkflows
      : jobWorkflows.filter((workflow) => workflow.workflow.bookingType === viewFilter || workflow.workflow.bookingType === null);

    // Prefer bookingType-specific workflow, fall back to global (bookingType=null), fall back to first.
    const preferredWorkflow = viewFilter === 'all'
      ? jobWorkflows.find((workflow) => workflow.workflow.bookingType === null)
      : jobWorkflows.find((workflow) => workflow.workflow.bookingType === viewFilter);
    const fallbackWorkflow = jobWorkflows.find((workflow) => workflow.workflow.bookingType === null) || jobWorkflows[0];
    const baseWorkflow = preferredWorkflow || fallbackWorkflow;

    const statusMap = new Map<string, WorkflowStatus>();

    // Seed ordering/labels from the base workflow first
    for (const status of baseWorkflow?.statuses || []) {
      if (status.showInJobboard && status.isActive) {
        statusMap.set(status.code, status);
      }
    }

    // Then add any missing statuses from other workflows (union)
    for (const wf of relevantWorkflows) {
      for (const status of wf.statuses || []) {
        if (!status.showInJobboard || !status.isActive) continue;
        if (!statusMap.has(status.code)) {
          statusMap.set(status.code, status);
        }
      }
    }

    // Sort by sortOrder, but keep stable order for equal sortOrder via code
    const sortedStatuses = Array.from(statusMap.values()).sort((a, b) => {
      if (a.sortOrder !== b.sortOrder) return a.sortOrder - b.sortOrder;
      return a.code.localeCompare(b.code);
    });
    const cols = sortedStatuses.map((s) => s.code);

    return {
      columns: cols,
      workflowStatuses: sortedStatuses,
    };
  }, [workflows, viewFilter]);

  const statusMetadata = useMemo(() => {
    return Object.fromEntries(
      workflowStatuses.map((status) => [
        status.code,
        {
          label: status.name,
          textColor: status.color,
          bgColor: status.bgColor,
          borderColor: status.borderColor,
        },
      ])
    );
  }, [workflowStatuses]);

  // Fetch jobs
  const { jobs, isLoading, isError, error, refetch } = useJobs({
    search: search || undefined,
  });

  // Filter jobs by booking type
  const filteredJobs = viewFilter === 'all'
    ? jobs
    : jobs.filter(job => job.bookingType === viewFilter);

  // Count jobs by type for badges - only count jobs in visible board columns
  const jobCounts = useMemo(() => {
    // Only count jobs whose status is in the visible columns
    const visibleJobs = jobs.filter(job => columns.includes(job.status));

    return {
      all: visibleJobs.length,
      private: visibleJobs.filter(j => j.bookingType === 'private').length,
      insurance: visibleJobs.filter(j => j.bookingType === 'insurance').length,
      warranty: visibleJobs.filter(j => j.bookingType === 'warranty').length,
    };
  }, [jobs, columns]);

  // Read jobId or bookingId from URL params
  useEffect(() => {
    const jobIdParam = searchParams.get('jobId');
    const bookingIdParam = searchParams.get('bookingId');

    if (jobIdParam) {
      setSelectedJobId(jobIdParam);
    } else if (bookingIdParam && jobs.length > 0) {
      // Find job by bookingId
      const job = jobs.find(j => j.bookingId === bookingIdParam);
      if (job) {
        setSelectedJobId(job.id);
      }
    }
  }, [searchParams, jobs]);

  // Fetch selected job detail
  const {
    job: selectedJob,
    timeline,
    isLoading: isLoadingDetail,
    refetch: refetchJob,
  } = useJobDetail(selectedJobId);

  // Mutations
  const { transition, isTransitioning } = useJobTransition();
  const { addEstimate, isAdding: isAddingEstimate } = useJobEstimate();
  const { addNote, isAdding: isAddingNote } = useJobNote();

  // Estimate line items & resend email
  const { data: estimateLinesData } = useEstimateLines(selectedJobId);
  const saveEstimateLines = useSaveEstimateLines();
  const resendEmail = useResendJobEmail();

  // Redirect to Case view when job is loaded (Case-First architecture)
  useEffect(() => {
    if (selectedJob?.caseId && !isLoadingDetail) {
      navigate(`/cases/${selectedJob.caseId}`);
    }
  }, [selectedJob?.caseId, isLoadingDetail, navigate]);

  // UI state for detail panel
  const [showEstimateModal, setShowEstimateModal] = useState(false);
  const [estimateAmount, setEstimateAmount] = useState('');
  const [estimateNotes, setEstimateNotes] = useState('');
  const [quickNote, setQuickNote] = useState('');

  // Quick transition dialog state (for job board cards)
  const [quickTransitionDialog, setQuickTransitionDialog] = useState<{
    isOpen: boolean;
    job: JobBoardItem | null;
    fromStatus: JobStatus;
    toStatus: JobStatus;
    transitionId: string;
    dialogType: string;
    dialogConfig?: Record<string, unknown> | null;
    requirements?: StatusChecklistRequirement[];
    canExecute?: boolean;
    errors?: string[];
  } | null>(null);
  const [quickTransitionError, setQuickTransitionError] = useState<Error | null>(null);

  // Handle job click from board
  const handleJobClick = useCallback((job: JobBoardItem) => {
    // Prefer case-centric navigation when caseId is available
    if (job.caseId) {
      navigate(`/cases/${job.caseId}`);
      return;
    }

    setSelectedJobId(job.id);
    // Update URL without triggering navigation
    setSearchParams({ jobId: job.id }, { replace: true });
  }, [navigate, setSearchParams]);

  // Handle quick transition from board (without opening detail panel)
  // Validation is done by the API - transitions only appear if workflow allows them
  const handleQuickTransition = useCallback(
    async (job: JobBoardItem, newStatus: string) => {
      if (isTransitioning) return;
      const fromStatus = job.status as JobStatus;
      const toStatus = newStatus as JobStatus;

      const availableTransitions = await api.get<Array<{
        transitionId: string;
        toStatus: string;
        dialogType: string;
        dialogConfig?: Record<string, unknown> | null;
        requirements?: StatusChecklistRequirement[];
        canExecute?: boolean;
        errors?: string[];
      }>>(`/api/jobs/${job.id}/available-transitions`);

      const matchedTransition = availableTransitions.find((item) => item.toStatus === toStatus);
      if (!matchedTransition) {
        return;
      }

      const needsDialog =
        matchedTransition.dialogType !== 'none' ||
        (matchedTransition.requirements && matchedTransition.requirements.length > 0) ||
        (matchedTransition.errors && matchedTransition.errors.length > 0);

      // If dialog is needed, show it
      if (needsDialog) {
        setQuickTransitionDialog({
          isOpen: true,
          job,
          fromStatus,
          toStatus,
          transitionId: matchedTransition.transitionId,
          dialogType: matchedTransition.dialogType,
          dialogConfig: matchedTransition.dialogConfig,
          requirements: matchedTransition.requirements,
          canExecute: matchedTransition.canExecute,
          errors: matchedTransition.errors,
        });
        setQuickTransitionError(null);
        return;
      }

      // No dialog needed, execute immediately
      try {
        await transition(job.id, newStatus, undefined, matchedTransition.transitionId);
        refetch();
      } catch {
        return;
      }
    },
    [transition, refetch, isTransitioning]
  );

  // Execute quick transition after dialog confirmation
  const handleExecuteQuickTransition = useCallback(
    async ({ notes, reason, bayId, lineItems, checklistValues, customFields }: {
      notes?: string;
      publicNote?: string;
      reason?: string;
      bayId?: string;
      lineItems?: Array<{
        id: string;
        description: string;
        quantity: number;
        unitPrice: number;
        category: string;
      }>;
      checklistValues?: Array<{ itemKey: string; value: boolean | number | string | Record<string, unknown> | null }>;
      customFields?: Record<string, unknown>;
    }) => {
      if (!quickTransitionDialog?.job) return;

      try {
        // Combine notes with reason if cancellation
        const transitionNote = reason
          ? `Motivo: ${reason}${notes ? `. ${notes}` : ''}`
          : notes;
        await transition(
          quickTransitionDialog.job.id,
          quickTransitionDialog.toStatus,
          transitionNote,
          quickTransitionDialog.transitionId,
          {
            notes,
            publicNote: undefined,
            bayId,
            estimateLines: lineItems?.map((item) => ({
              description: item.description,
              type: (item.category || 'labor') as 'labor' | 'parts' | 'materials' | 'other',
              quantity: item.quantity,
              unitPrice: item.unitPrice,
              total: item.unitPrice * item.quantity,
            })),
            customFields: { ...customFields, cancellationReason: reason },
          },
          checklistValues
        );

        setQuickTransitionDialog(null);
        setQuickTransitionError(null);
        refetch();
      } catch (error) {
        setQuickTransitionError(error instanceof Error ? error : new Error('Error al cambiar estado'));
      }
    },
    [quickTransitionDialog, transition, refetch]
  );

  // Cancel quick transition dialog
  const handleCancelQuickTransition = useCallback(() => {
    setQuickTransitionDialog(null);
    setQuickTransitionError(null);
  }, []);

  // Handle add estimate
  const handleAddEstimate = useCallback(
    async () => {
      const amount = parseFloat(estimateAmount);
      if (selectedJobId && !isNaN(amount) && amount >= 0 && estimateNotes.trim()) {
        await addEstimate(selectedJobId, amount, estimateNotes);
        setShowEstimateModal(false);
        setEstimateAmount('');
        setEstimateNotes('');
        refetchJob();
      }
    },
    [selectedJobId, estimateAmount, estimateNotes, addEstimate, refetchJob]
  );

  // Handle add note
  const handleAddQuickNote = useCallback(
    async () => {
      if (selectedJobId && quickNote.trim()) {
        await addNote(selectedJobId, quickNote.trim());
        setQuickNote('');
        refetchJob();
      }
    },
    [selectedJobId, quickNote, addNote, refetchJob]
  );

  // Close detail drawer
  const handleCloseDetail = useCallback(() => {
    setSelectedJobId(null);
    setSearchParams({}, { replace: true });
  }, [setSearchParams]);

  // Format helpers
  const formatDateTime = (value: number | Date) => {
    const date = value instanceof Date ? value : new Date(value * 1000);
    return date.toLocaleString('es-ES', { dateStyle: 'short', timeStyle: 'short' });
  };

  const { data: detailTransitions = [] } = useQuery({
    queryKey: ['jobs', selectedJobId, 'available-transitions'],
    queryFn: () => api.get<Array<{
      transitionId: string;
      toStatus: string;
      dialogType: string;
      dialogConfig?: Record<string, unknown> | null;
      requirements?: StatusChecklistRequirement[];
      canExecute?: boolean;
      errors?: string[];
    }>>(`/api/jobs/${selectedJobId}/available-transitions`),
    enabled: !!selectedJobId,
  });


  const selectedJobBoardItem = useMemo<JobBoardItem | null>(() => {
    if (!selectedJob) return null;
    return {
      id: selectedJob.id,
      bookingId: selectedJob.bookingId,
      bookingReference: selectedJob.bookingReference,
      bookingType: (['private', 'insurance', 'warranty'].includes(selectedJob.booking?.bookingType || '')
        ? (selectedJob.booking?.bookingType as JobBoardItem['bookingType'])
        : undefined),
      status: selectedJob.status as JobStatus,
      estimateAmount: selectedJob.estimateAmount,
      approvalStatus: (selectedJob.approvalStatus as JobBoardItem['approvalStatus']) || null,
      createdAt: selectedJob.createdAt,
      scheduledAt: selectedJob.booking?.scheduledAt,
      customer: selectedJob.customer,
      vehicle: selectedJob.vehicle,
      service: selectedJob.service,
      bay: selectedJob.bay,
      assignedStaff: selectedJob.assignedStaff,
    };
  }, [selectedJob]);

  const handleDetailTransition = useCallback(
    (status: string) => {
      if (!selectedJobBoardItem) return;
      handleQuickTransition(selectedJobBoardItem, status);
    },
    [handleQuickTransition, selectedJobBoardItem]
  );

  return (
    <div className="h-full flex flex-col">
      {/* Header - standardized CRM styling */}
      <div className="bg-white border-b px-6 py-3">
        <div className={cn(pageStyles.header, 'flex-col gap-3 sm:flex-row sm:items-center')}>
          <div>
            <h1 className={pageStyles.title}>Tablero de Trabajos</h1>
            <p className={pageStyles.subtitle}>
              Gestión del flujo de trabajo del taller
            </p>
          </div>

          <div className="flex w-full items-center gap-2 sm:w-auto">
            {/* Search */}
            <div className="relative">
              <Search className={formStyles.searchIcon} />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar matrícula o cliente..."
                className={cn(formStyles.inputWithIcon, 'w-full sm:w-56')}
              />
              {search && (
                <button
                  onClick={() => setSearch('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X className={iconSizes.sm} />
                </button>
              )}
            </div>

            {/* Refresh */}
            <button
              onClick={() => refetch()}
              disabled={isLoading}
              className={buttonStyles.icon}
              title="Actualizar"
            >
              <RefreshCw className={cn(iconSizes.md, isLoading && 'animate-spin')} />
            </button>
          </div>
        </div>

        {/* View filter tabs */}
        <div className="-mx-1 flex gap-1 mt-3 border-t pt-3 overflow-x-auto px-1 scrollbar-hide">
          {VIEW_FILTERS.map(({ value, label, icon: Icon }) => (
            <button
              key={value}
              onClick={() => setViewFilter(value)}
              className={cn(
                'flex shrink-0 items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-colors whitespace-nowrap',
                viewFilter === value
                  ? value === 'private'
                    ? 'bg-blue-100 text-blue-700 border border-blue-200'
                    : value === 'insurance'
                    ? 'bg-amber-100 text-amber-700 border border-amber-200'
                    : value === 'warranty'
                    ? 'bg-purple-100 text-purple-700 border border-purple-200'
                    : 'bg-gray-100 text-gray-700 border border-gray-200'
                  : 'text-gray-500 hover:bg-gray-50'
              )}
            >
              <Icon className="h-3.5 w-3.5" />
              <span>{label}</span>
              <span className={cn(
                'px-1.5 py-0.5 rounded-full text-xs',
                viewFilter === value ? 'bg-white/50' : 'bg-gray-100'
              )}>
                {jobCounts[value]}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Content area with drawer */}
      <div className="flex-1 flex overflow-hidden">
        {/* Main board area */}
        <div
          className={cn(
            'flex-1 overflow-auto p-6 transition-all duration-300 scrollbar-hide',
            selectedJobId && 'pr-0 lg:pr-6'
          )}
        >
          {isError ? (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <h3 className="text-red-800 font-medium">Error al cargar los trabajos</h3>
              <p className="text-red-600 text-sm mt-1">
                {error?.message || 'Ha ocurrido un error desconocido'}
              </p>
              <button
                onClick={() => refetch()}
                className="mt-2 text-sm text-red-600 hover:text-red-800 underline"
              >
                Reintentar
              </button>
            </div>
          ) : (
            <JobBoard
              jobs={filteredJobs}
              columns={columns}
              workflowStatuses={workflowStatuses}
              onJobClick={handleJobClick}
              onQuickTransition={handleQuickTransition}
              loading={isLoading}
            />
          )}
        </div>

        {/* Detail drawer */}
        {selectedJobId && (
          <div className="w-full lg:w-[500px] xl:w-[560px] border-l bg-background flex flex-col overflow-hidden">
            {/* Drawer header */}
            <div className="flex items-center justify-between px-4 py-3 bg-card border-b">
              <div className="flex items-center gap-2">
                <button
                  onClick={handleCloseDetail}
                  className="p-1.5 hover:bg-muted rounded-md lg:hidden"
                >
                  <ChevronRight className="h-5 w-5" />
                </button>
                <h2 className="font-semibold text-foreground">Detalle del Trabajo</h2>
              </div>
              <button
                onClick={handleCloseDetail}
                className="p-1.5 hover:bg-muted rounded-md hidden lg:block"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Drawer content */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4 scrollbar-hide">
              {isLoadingDetail ? (
                <JobDetailSkeleton />
              ) : selectedJob ? (
                <>
                  {/* Status Card */}
                  <StatusCard
                    status={selectedJob.status}
                    transitions={detailTransitions}
                    onTransitionClick={handleDetailTransition}
                    updatedAt={selectedJob.updatedAt}
                    workflowStatuses={workflowStatuses}
                  />

                  {/* Case / Booking links */}
                  {selectedJob.booking?.caseId && (
                    <Link
                      to={`/cases/${selectedJob.booking.caseId}`}
                      className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 hover:underline"
                    >
                      <span>Ver caso</span>
                      <ExternalLink className="h-3.5 w-3.5" />
                    </Link>
                  )}

                  {selectedJob.bookingId && (
                    <Link
                      to={`/bookings/${selectedJob.bookingId}`}
                      className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 hover:underline"
                    >
                      <span>Ver reserva {selectedJob.bookingReference || ''}</span>
                      <ExternalLink className="h-3.5 w-3.5" />
                    </Link>
                  )}

                  {/* Info Cards Grid */}
                  <div className="grid grid-cols-1 gap-3">
                    {/* Vehicle */}
                    <InfoCard
                      icon={Car}
                      title="Vehículo"
                      iconColor="text-blue-600"
                    >
                      {selectedJob.vehicle ? (
                        <div className="space-y-1">
                          <p className="text-lg font-mono font-bold">{selectedJob.vehicle.registration}</p>
                          <p className="text-sm text-muted-foreground">
                            {selectedJob.vehicle.make} {selectedJob.vehicle.model}
                            {selectedJob.vehicle.year && ` (${selectedJob.vehicle.year})`}
                          </p>
                        </div>
                      ) : (
                        <p className="text-sm text-muted-foreground">Sin información</p>
                      )}
                    </InfoCard>

                    {/* Customer */}
                    <InfoCard
                      icon={User}
                      title="Cliente"
                      iconColor="text-green-600"
                    >
                      {selectedJob.customer ? (
                        <div className="space-y-1.5">
                          <p className="font-medium">{selectedJob.customer.name}</p>
                          <div className="flex flex-wrap gap-3 text-sm">
                            <a
                              href={`tel:${selectedJob.customer.phone}`}
                              className="flex items-center gap-1.5 text-muted-foreground hover:text-foreground"
                            >
                              <Phone className="h-3.5 w-3.5" />
                              {selectedJob.customer.phone}
                            </a>
                            <a
                              href={`mailto:${selectedJob.customer.email}`}
                              className="flex items-center gap-1.5 text-muted-foreground hover:text-foreground truncate"
                            >
                              <Mail className="h-3.5 w-3.5" />
                              {selectedJob.customer.email}
                            </a>
                          </div>
                        </div>
                      ) : (
                        <p className="text-sm text-muted-foreground">Sin información</p>
                      )}
                    </InfoCard>

                    {/* Service & Assignment */}
                    <div className="grid grid-cols-2 gap-3">
                      <InfoCard
                        icon={Wrench}
                        title="Servicio"
                        iconColor="text-purple-600"
                        compact
                      >
                        {selectedJob.service ? (
                          <div>
                            <p className="text-sm font-medium truncate">{selectedJob.service.name}</p>
                            <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                              <Clock className="h-3 w-3" />
                              {selectedJob.service.durationMinutes} min
                            </p>
                          </div>
                        ) : (
                          <p className="text-xs text-muted-foreground">Sin asignar</p>
                        )}
                      </InfoCard>

                      <InfoCard
                        icon={MapPin}
                        title="Bahía"
                        iconColor="text-orange-600"
                        compact
                      >
                        {selectedJob.bay ? (
                          <div>
                            <p className="text-sm font-medium">{selectedJob.bay.name}</p>
                            <p className="text-xs text-muted-foreground capitalize">{selectedJob.bay.bayType}</p>
                          </div>
                        ) : (
                          <p className="text-xs text-muted-foreground">Sin asignar</p>
                        )}
                    </InfoCard>
                  </div>

                  {selectedJob.bookingImages && selectedJob.bookingImages.length > 0 && (
                    <Card>
                      <CardHeader className="flex flex-row items-center gap-2 pb-2">
                        <Image className="h-4 w-4 text-amber-600" />
                        <span className="text-sm font-semibold">Fotos del cliente</span>
                      </CardHeader>
                      <CardContent className="grid grid-cols-2 gap-2">
                        {selectedJob.bookingImages.map((image) => (
                          <a
                            key={image.id}
                            href={image.url}
                            target="_blank"
                            rel="noreferrer"
                            className="group relative overflow-hidden rounded-md border"
                          >
                            <img
                              src={image.url}
                              alt="Foto del vehículo"
                              className="h-24 w-full object-cover transition-transform duration-200 group-hover:scale-105"
                            />
                          </a>
                        ))}
                      </CardContent>
                    </Card>
                  )}
                  </div>

                  {/* Estimate Builder */}
                  <EstimateEditor
                    jobId={selectedJob.id}
                    bookingId={selectedJob.bookingId}
                    initialLineItems={
                      (estimateLinesData?.lineItems || []).map((item: EstimateLineItem & { totalPrice?: number }) => ({
                        id: item.id,
                        description: item.description,
                        category: item.category || 'labor',
                        quantity: item.quantity,
                        unitPrice: item.unitPrice,
                      }))
                    }
                    initialNotes={selectedJob.estimateNotes || ''}
                    onSave={async (lineItems, notes) => {
                      await saveEstimateLines.mutateAsync({
                        jobId: selectedJob.id,
                        lineItems: lineItems.map(item => ({
                          description: item.description,
                          category: item.category,
                          quantity: item.quantity,
                          unitPrice: item.unitPrice,
                        })),
                        notes,
                      });
                      refetchJob();
                    }}
                    isSaving={saveEstimateLines.isPending}
                  />

                  {/* Approval Status */}
                  {selectedJob.approvalStatus && (
                    <Card>
                      <CardContent className="pt-4 space-y-2">
                        <Badge
                          variant={selectedJob.approvalStatus === 'approved' ? 'default' : 'secondary'}
                          className={cn(
                            'text-xs',
                            selectedJob.approvalStatus === 'approved' && 'bg-green-600'
                          )}
                        >
                          {selectedJob.approvalStatus === 'approved' ? (
                            <><CheckCircle className="h-3 w-3 mr-1" /> Aprobado</>
                          ) : selectedJob.approvalStatus === 'rejected' ? (
                            <><AlertCircle className="h-3 w-3 mr-1" /> Rechazado</>
                          ) : (
                            <><Clock className="h-3 w-3 mr-1" /> Pendiente</>
                          )}
                        </Badge>
                        {(selectedJob.approvedAt || selectedJob.approvedBy) && (
                          <p className="text-xs text-muted-foreground">
                            {selectedJob.approvedAt ? `Aprobado ${formatDateTime(selectedJob.approvedAt)}` : 'Aprobado'}
                            {selectedJob.approvedBy ? ` por ${selectedJob.approvedBy}` : ''}
                          </p>
                        )}
                      </CardContent>
                    </Card>
                  )}

                  {/* Resend Email */}
                  {selectedJob.customer?.email && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full"
                      onClick={() => resendEmail.mutate(selectedJob.id)}
                      disabled={resendEmail.isPending}
                    >
                      {resendEmail.isPending ? (
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      ) : (
                        <Send className="h-4 w-4 mr-2" />
                      )}
                      Reenviar notificación
                    </Button>
                  )}

                  {/* Quick Note */}
                  <Card>
                    <CardContent className="pt-4">
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={quickNote}
                          onChange={(e) => setQuickNote(e.target.value)}
                          placeholder="Añadir nota rápida..."
                          className="flex-1 h-9 px-3 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && quickNote.trim()) {
                              handleAddQuickNote();
                            }
                          }}
                        />
                        <Button
                          size="sm"
                          onClick={handleAddQuickNote}
                          disabled={!quickNote.trim() || isAddingNote}
                          className="h-9"
                        >
                          {isAddingNote ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Añadir'}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Timeline */}
                  <div>
                    <h3 className="text-sm font-medium text-muted-foreground mb-3">Línea de tiempo</h3>
                    <JobTimeline events={(timeline || []) as TimelineEventData[]} />
                  </div>
                </>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  No se pudo cargar el trabajo
                </div>
              )}
            </div>

            {/* Estimate Modal */}
            {showEstimateModal && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                <Card className="w-full max-w-md mx-4">
                  <CardHeader>
                    <h3 className="text-lg font-semibold">
                      {selectedJob?.estimateAmount !== null ? 'Actualizar presupuesto' : 'Añadir presupuesto'}
                    </h3>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium mb-1.5">
                        Importe (EUR)
                      </label>
                      <input
                        type="number"
                        value={estimateAmount}
                        onChange={(e) => setEstimateAmount(e.target.value)}
                        min="0"
                        step="0.01"
                        className="w-full h-10 px-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
                        placeholder="0.00"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-1.5">
                        Descripción
                      </label>
                      <textarea
                        value={estimateNotes}
                        onChange={(e) => setEstimateNotes(e.target.value)}
                        rows={3}
                        className="w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
                        placeholder="Detalles del presupuesto..."
                      />
                    </div>

                    <div className="flex gap-2 pt-2">
                      <Button
                        variant="outline"
                        className="flex-1"
                        onClick={() => {
                          setShowEstimateModal(false);
                          setEstimateAmount('');
                          setEstimateNotes('');
                        }}
                      >
                        Cancelar
                      </Button>
                      <Button
                        className="flex-1"
                        onClick={handleAddEstimate}
                        disabled={!estimateAmount || !estimateNotes.trim() || isAddingEstimate}
                      >
                        {isAddingEstimate && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                        Guardar
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </div>
        )}

        {/* Quick Transition Dialog for Job Board cards */}
        {quickTransitionDialog && (
          <StatusTransitionDialog
            state={{
              isOpen: quickTransitionDialog.isOpen,
              bookingId: quickTransitionDialog.job?.bookingId || '',
              fromStatus: quickTransitionDialog.fromStatus,
              toStatus: quickTransitionDialog.toStatus,
              transitionId: quickTransitionDialog.transitionId,
              config: {
                dialogType: quickTransitionDialog.dialogType,
                dialogConfig: quickTransitionDialog.dialogConfig,
                requirements: quickTransitionDialog.requirements,
                canExecute: quickTransitionDialog.canExecute,
                errors: quickTransitionDialog.errors,
              },
              bookingReference: quickTransitionDialog.job?.bookingReference || undefined,
              customerName: quickTransitionDialog.job?.customer?.name,
            }}
            isTransitioning={isTransitioning}
            error={quickTransitionError}
            onExecute={handleExecuteQuickTransition}
            onCancel={handleCancelQuickTransition}
            serviceBayType={quickTransitionDialog.job?.service?.bayType}
            statusMetadata={statusMetadata}
          />
        )}
      </div>
    </div>
  );
}

// ============================================================================
// Sub-components
// ============================================================================

interface StatusCardProps {
  status: string;
  transitions: Array<{
    transitionId: string;
    toStatus: string;
    canExecute?: boolean;
    errors?: string[];
  }>;
  onTransitionClick: (status: string) => void;
  updatedAt: number;
  workflowStatuses: WorkflowStatus[];
}

function StatusCard({
  status,
  transitions,
  onTransitionClick,
  updatedAt,
  workflowStatuses,
}: StatusCardProps) {
  const config = getStatusConfig(status, workflowStatuses);
  const Icon = config?.icon;
  const getTransitionTooltipContent = (transition: StatusCardProps['transitions'][number]) => {
    if (transition.canExecute === false) {
      if (transition.errors && transition.errors.length > 0) {
        return (
          <div className="space-y-1">
            <p className="font-medium">Bloqueado</p>
            <ul className="list-disc pl-4 text-xs space-y-0.5">
              {transition.errors.map((error) => (
                <li key={error}>{error}</li>
              ))}
            </ul>
          </div>
        );
      }
      return 'Transicion no disponible';
    }

    return 'Cumple requisitos para transicionar';
  };

  return (
    <Card
      className={cn('border-l-4', !config?.textColor && config?.color?.replace('text-', 'border-l-'))}
      style={config?.textColor ? { borderLeftColor: config.textColor } : undefined}
    >
      <CardContent className="pt-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-3">
            {Icon && (
              <div
                className={cn('p-2 rounded-lg', !config?.backgroundColor && config?.bgColor)}
                style={config?.backgroundColor ? { backgroundColor: config.backgroundColor } : undefined}
              >
                <Icon
                  className={cn('h-5 w-5', !config?.textColor && config?.color)}
                  style={config?.textColor ? { color: config.textColor } : undefined}
                />
              </div>
            )}
            <div>
              <p
                className={cn('font-semibold', !config?.textColor && config?.color)}
                style={config?.textColor ? { color: config.textColor } : undefined}
              >
                {config?.label || status}
              </p>
              <p className="text-xs text-muted-foreground">
                {new Date(updatedAt * 1000).toLocaleString('es-ES', {
                  dateStyle: 'short',
                  timeStyle: 'short',
                })}
              </p>
            </div>
          </div>

          {transitions.length > 0 && (
            <div className="flex flex-wrap gap-1.5 justify-end">
              {transitions.slice(0, 3).map((transition) => {
                const nextStatus = transition.toStatus;
                const nextConfig = getStatusConfig(nextStatus, workflowStatuses);
                return (
                  <div key={nextStatus} className="relative">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onTransitionClick(nextStatus)}
                      disabled={false}
                      className={cn('h-7 text-xs gap-1 pr-6', !nextConfig?.textColor && nextConfig?.color)}
                      style={
                        nextConfig?.textColor
                          ? {
                              color: nextConfig.textColor,
                              backgroundColor: nextConfig.backgroundColor || undefined,
                              borderColor: nextConfig.borderColor || undefined,
                            }
                          : undefined
                      }
                    >
                      {nextConfig?.label || nextStatus}
                      <ChevronRight className="h-3 w-3" />
                    </Button>
                    {transition.canExecute === false && (
                      <SimpleTooltip content={getTransitionTooltipContent(transition)} side="left">
                        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground">
                          <Info className="h-3.5 w-3.5" />
                        </span>
                      </SimpleTooltip>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

interface InfoCardProps {
  icon: typeof Car;
  title: string;
  iconColor?: string;
  compact?: boolean;
  children: React.ReactNode;
}

function InfoCard({ icon: Icon, title, iconColor = 'text-muted-foreground', compact, children }: InfoCardProps) {
  return (
    <Card>
      <CardContent className={cn('pt-3', compact ? 'pb-3' : 'pb-4')}>
        <div className="flex items-center gap-2 mb-2">
          <Icon className={cn('h-4 w-4', iconColor)} />
          <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{title}</h3>
        </div>
        {children}
      </CardContent>
    </Card>
  );
}

function JobDetailSkeleton() {
  return (
    <div className="space-y-4">
      <Skeleton className="h-20 rounded-lg" />
      <Skeleton className="h-24 rounded-lg" />
      <Skeleton className="h-24 rounded-lg" />
      <div className="grid grid-cols-2 gap-3">
        <Skeleton className="h-20 rounded-lg" />
        <Skeleton className="h-20 rounded-lg" />
      </div>
      <Skeleton className="h-32 rounded-lg" />
    </div>
  );
}
